<template>
  <span>
    <div
      v-if="!latLng || !showPotree"
      class="fit"
    >
      <iframe
        name="mapiaPano"
        v-if="!showLateral"
        :src="srcPannellum"
        @load="loadPanoramaFrame"
        frameborder="0"
        :class="horizontal && 'fit no-margin'"
        :style="!horizontal && `height: calc(${offset} - 5px); width: 100%`"
      />
      <pinch-zoom v-else>
        <img
          :src="srcLateral"
          :style="horizontal ? `height: calc(${offset} - 20px)` : `height: ${offset}`"
        />
      </pinch-zoom>
    </div>
    <div
      v-else-if="latLng && !showPannellum"
      class="fit"
    >
      <iframe
        name="mapiaPointCloud"
        :src="srcPotree"
        @load="movePointCloudFrame"
        class="fit no-margin"
        frameborder="0"
      />
    </div>
    <q-splitter
      v-else-if="latLng"
      v-model="splitterModel"
      :horizontal="horizontal"
      :style="horizontal && `height: ${splitterHeight}`"
    >
      <template v-slot:before>
        <iframe
          name="mapiaPano"
          v-if="!showLateral"
          :src="srcPannellum"
          @load="loadPanoramaFrame"
          :style="horizontal ? `height: ${iframeHeightBefore}; width: 100%` : `height: calc(${offset} - 8px); width: 100%`"
          frameborder="0"
        />
        <pinch-zoom
          v-else
          :style="horizontal ? `height: ${iframeHeightBefore}` : `height: ${offset}`"
        >
          <img :src="srcLateral"/>
        </pinch-zoom>
      </template>
      <template v-slot:separator>
        <q-avatar color="primary" text-color="white" size="30px" :icon="horizontal ? 'unfold_more' : 'code'" />
      </template>
      <template v-slot:after>
        <iframe
          name="mapiaPointCloud"
          :src="srcPotree"
          @load="movePointCloudFrame"
          :style="horizontal ? `height: ${iframeHeightAfter}; width: 100%` : `height: calc(${offset} - 8px); width: 100%`"
          frameborder="0"
        />
      </template>
    </q-splitter>
  </span>
</template>

<script>
import PinchZoom from 'vue-pinch-zoom'
import { QAvatar, QSplitter } from 'quasar'
import { mapState } from 'vuex'
import proj4 from 'proj4'
import { calculateTargetPoint } from 'src/lib/geometry'
import { projections } from 'src/lib/projections'

export default {
  props: ['horizontal', 'offset'],
  components: {
    PinchZoom,
    QAvatar,
    QSplitter
  },
  beforeMount () {
    proj4.defs(projections)
  },
  mounted () {
    this.$nextTick(() => {
      this.addEventListener()
    })
  },
  data () {
    return {
      splitterModel: 50 // start at 50%
    }
  },
  computed: {
    ...mapState({
      username: state => state.auth.username,
      altitude: state => state.panorama.altitude,
      head: state => state.panorama.head,
      headFrom: state => state.panorama.headFrom,
      latLng: state => state.panorama.latLng,
      name: state => state.panorama.name,
      pan: state => state.panorama.pan,
      pitch: state => state.panorama.pitch,
      roll: state => state.panorama.roll,
      selectedCampaign: state => state.panorama.selectedCampaign,
      srcPannellum: state => state.panorama.srcPannellum,
      srcLateral: state => state.panorama.srcLateral,
      srcPotree: state => state.panorama.srcPotree,
      showPotree: state => state.panorama.showPotree,
      showLateral: state => state.panorama.showLateral,
      showPannellum: state => state.panorama.showPannellum
    }),
    splitterHeight () {
      return `calc(${this.offset} - 20px)`
    },
    iframeHeightBefore () {
      return `calc(${this.offset} * ${this.splitterModel} / 100 - 20px)`
    },
    iframeHeightAfter () {
      return `calc(${this.offset} * ${100 - this.splitterModel} / 100 - 20px)`
    }
  },
  methods: {
    addEventListener: function () {
      const self = this
      // Listen to message from iframe element
      window.addEventListener('message', function (msg) {
        if (msg.data && msg.data.type === 'rotation') {
          const head = 90 - (msg.data.head + self.pan)
          if (!Number.isNaN(head)) {
            self.$store.commit('panorama/setHead', { head, from: 'pannellum' })
          }
        } else if (msg.data && msg.data.type === 'nextpano') {
          const latLng = {
            lat: parseFloat(msg.data.lat),
            lng: parseFloat(msg.data.lng)
          }
          self.$store.dispatch('panorama/getPanoByPoint', latLng)
          const head = 90 - (msg.data.asimuth + self.pan)
          if (!Number.isNaN(head)) {
            self.$store.commit('panorama/setHead', { head, from: 'pannellum' })
          }
        }
      })
    },
    loadPanoramaFrame () {
      this.updatePanoramaNeighbours()
      this.rotatePanoramaFrame()
    },
    updatePanoramaNeighbours () {
      if (!this.srcPannellum || !this.name) {
        return
      }
      const iframe = window.frames['mapiaPano']
      if (iframe && iframe.mapiaPano && iframe.mapiaPano.updateNeighbours) {
        const neighbours = this.$store.state.panorama.neighbours
        const pointNeighbours = neighbours.map(neighbour => {
          const coords = [...neighbour.geom.coordinates]
          const point = [...coords.reverse(), neighbour.altitude]
          return point.join(',')
        }).join('|')
        iframe.mapiaPano.updateNeighbours(pointNeighbours)
      }
    },
    rotatePanoramaFrame () {
      if (!this.srcPannellum || !this.name) {
        return
      }
      const iframe = window.frames['mapiaPano']
      if (this.headFrom !== 'pannellum' && iframe && iframe.rotatePanorama) {
        const rotation = 90 - (this.head + this.pan)
        iframe.rotatePanorama(rotation)
      }
    },
    movePointCloudFrame () {
      if (!this.srcPotree) {
        return
      }
      const iframe = window.frames['mapiaPointCloud']
      if (this.latLng && this.head && iframe && iframe.mapiaPotree) {
        const latLngProj = proj4('EPSG:4326', `EPSG:${this.$config.panoramas.epsg}`).forward([this.latLng.lng, this.latLng.lat])
        const latLngTarget = calculateTargetPoint(latLngProj, this.head, 100)
        const positionPointCloud = [latLngProj[0], latLngProj[1], this.altitude]
        const targetPointCloud = [latLngTarget[0], latLngTarget[1], this.altitude]
        iframe.mapiaPotree.moveTo(positionPointCloud, targetPointCloud)
      }
    }
  },
  watch: {
    latLng: ['movePointCloudFrame'],
    head: ['rotatePanoramaFrame', 'movePointCloudFrame'],
    srcPannellum: function () {
      this.rotatePanoramaFrame()
    },
    srcPotree: function () {
      this.movePointCloudFrame()
    }
  }
}
</script>
